import { createUXTimings } from './25-ux-timings.mjs';
/** Content reads fail recoverably after10seconds; mutation outcomes are not guessed. */
export const READ_REQUEST_TIMEOUT_MS = 10000;
const readPaths = new Set(['/api/bootstrap', '/api/state', '/api/feed', '/api/recommendations']);
const boundedRead = (path, body) =>
  body ? path === '/api/feed/hydrate' : readPaths.has(path.split('?')[0]);
/** JSON transport for the canonical API. A late unauthorized response from an
 * earlier actor cannot sign out a newly selected account. Abort signals remain
 * composed with bounded content reads; writes retain caller cancellation. No response
 * bodies are cached by this boundary. */
export function createAPI({
  actor,
  onUnauthorized,
  send = fetch,
  readTimeoutMs = READ_REQUEST_TIMEOUT_MS,
  schedule = setTimeout,
  cancel = clearTimeout,
}) {
  const api = async function api(path, body, options = {}) {
    const owner = actor();
    const controller = boundedRead(path, body) ? new AbortController() : undefined;
    let timer, rejectStopped;
    const stopped = controller
      ? new Promise((_, reject) => {
          rejectStopped = reject;
        })
      : undefined;
    const abort = () => {
      const error =
        options.signal?.reason instanceof Error
          ? options.signal.reason
          : new DOMException('Request cancelled.', 'AbortError');
      controller.abort(error);
      rejectStopped(error);
    };
    if (controller) {
      if (options.signal?.aborted)
        throw options.signal.reason instanceof Error
          ? options.signal.reason
          : new DOMException('Request cancelled.', 'AbortError');
      options.signal?.addEventListener('abort', abort, { once: true });
      timer = schedule(() => {
        const error = new Error('This is taking too long. Try loading again.');
        error.name = 'TimeoutError';
        error.code = 'READ_REQUEST_TIMEOUT';
        controller.abort(error);
        rejectStopped(error);
      }, readTimeoutMs);
    }
    const transport = async () => {
      const response = await send(path, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body: body ? JSON.stringify(body) : undefined,
        keepalive: options.keepalive ?? false,
        signal: controller?.signal ?? options.signal,
      });
      const data = await response.json();
      if (!response.ok) {
        if (
          response.status === 401 &&
          !controller?.signal.aborted &&
          owner != null &&
          path.split('?')[0] !== '/api/login' &&
          actor() === owner
        )
          onUnauthorized();
        const error = new Error(data.error ?? 'Something went wrong. Try again.');
        error.status = response.status;
        throw error;
      }
      return data;
    };
    try {
      // The explicit race covers fetch AND body parsing even if an adapter ignores abort.
      return await (stopped ? Promise.race([transport(), stopped]) : transport());
    } finally {
      if (controller) {
        cancel(timer);
        options.signal?.removeEventListener('abort', abort);
      }
    }
  };
  api.timings = createUXTimings({
    actor,
    route: () => globalThis.location?.hash ?? '',
    send: (events) => api('/api/telemetry/ux', { events }),
  });
  return api;
}
